import { useCallback, useMemo } from 'react';
import { gate } from 'hub-http/gates';
import { ApolloProvider } from '@apollo/client';
import { createApolloClient } from 'hubspot-apollo-client';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { Button } from 'foundations-components/transitional/components/Button';
import { Link } from 'foundations-components/transitional/components/Link';
import { LockBadge } from 'foundations-components/transitional/components/LockBadge';
import { useUpgradeModal } from 'ui-addon-upgrades/ums/useUpgradeModal';
import AppLink from 'ui-forms-components/links/AppLink';
import { getPropertyValue } from 'marketing-platform-lib/utils/crmUtils';
import { useUserInfo } from 'marketing-platform-lib/providers/UserInfoProvider';
import { getFormFileUploadsRoute
// @ts-expect-error: no types
} from 'FormsCommonComponents/lib/RouteGenerators';
import FormTypes from 'forms-utils-lib/constants/formDefinition/FormTypes';
import { CREATE_POP_UP_CALLS_TO_ACTION } from 'forms-utils-lib/urls/constants/KCSArticlePaths';
import FormPermissionsActionPanel from 'ui-forms-components/permissions/FormPermissionsActionPanel';
import DocLink from 'ui-forms-components/links/DocLink';
import EmbedVersions from 'forms-utils-lib/constants/formDefinition/EmbedVersions';
import { ACTION_IDS } from 'asset-actions-lib/constants/actionIds';
import { useAssetActions } from 'asset-actions-lib/context/AssetActionsContext';
import { DELETE_ACTION } from 'asset-actions-lib/constants/sharedActions/delete';
import { COMMENT_ACTION } from 'asset-actions-lib/constants/sharedActions/comment';
import { MANAGE_ACCESS_ACTION } from 'asset-actions-lib/constants/sharedActions/manageAccess';
import { getCompareRoute } from 'asset-actions-lib/utils/routes';
import { FORM_ACTION_IDS } from 'asset-actions-lib-forms/constants/actionIds';
import ViewFormModal from 'asset-actions-lib-forms/view/ViewFormModal';
import SubmissionExportDialog from 'asset-actions-lib-forms/exportSubmissions/SubmissionExportDialog';
import FormDeleteModal from 'asset-actions-lib-forms/delete/FormDeleteModal';
import SelectCampaignModal from 'asset-actions-lib-forms/selectCampaign/SelectCampaignModal';
import { getCollectedFormsSettingsPage, getPerformanceRoute, getSubmissionsRoute } from 'asset-actions-lib-forms/utils/getRoute';
import TranslateFormDialog from 'asset-actions-lib-forms/createTranslation/components/TranslateFormDialog';
import CloneFormDialog from 'asset-actions-lib-forms/clone/components/CloneFormDialog';
import BusinessUnitTagContainer from 'asset-actions-lib-forms/manageAccess/BusinessUnitTagContainer';
import ListCreationModal from 'segments-lib/components/ListCreationModal';
import { getContactListFilterBranch } from 'asset-actions-lib-forms/utils/urlUtils';
import ShareFormDialog from 'asset-actions-lib-forms/share/components/ShareFormDialog';
import CollectedFormRenameDialog from 'asset-actions-lib-forms/rename/components/CollectedFormRenameDialog';
import EditNonHSMailchimpListActionButton from 'asset-actions-lib-forms/editNonHSMailchimpList/components/EditNonHSMailchimpListActionButton';
import ESPSettingsDialog from 'asset-actions-lib-forms/editNonHSMailchimpList/components/ESPSettingsDialog';
import UnpublishActionButton from 'asset-actions-lib-forms/unpublish/components/UnpublishActionButton';
import EditActionButton from 'asset-actions-lib-forms/edit/components/EditActionButton';
import SummarizeActionButton from 'asset-actions-lib-forms/summarize/components/SummarizeActionButton';
import CreateCustomerAgentActionButton from 'asset-actions-lib-forms/createCustomerAgentAction/components/CreateCustomerAgentActionButton';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const apolloClient = createApolloClient();
export const FORM_SUMMARIZE_ACTION = {
  id: FORM_ACTION_IDS.SUMMARIZE,
  name: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "assetActionsLibForms.actions.summarize"
  }),
  actionGroup: 0,
  orderInActionGroup: 0,
  displayPermissions: {
    custom: ({
      crmObject,
      gates
    }) => getPropertyValue(crmObject, 'hs_form_type') === FormTypes.HUBSPOT && getPropertyValue(crmObject, 'hs_embed_version') === EmbedVersions.V4 && gates.includes(gate('Forms:CopilotSummarize'))
  },
  DisabledTooltipContent: () => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "assetActionsLibForms.tooltip.noPermission"
  }),
  ActionButton: SummarizeActionButton
};
export const FORM_CREATE_CUSTOMER_AGENT_ACTION = {
  id: FORM_ACTION_IDS.CREATE_CUSTOMER_AGENT_ACTION,
  name: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "assetActionsLibForms.actions.createCustomerAgentAction"
  }),
  actionGroup: 0,
  orderInActionGroup: 1,
  displayPermissions: {
    custom: ({
      gates
    }) => gates.includes(gate('Forms:CustomerAgent')) && gates.includes(gate('CustomerAgent:Actions'))
  },
  enablePermissions: {
    scopes: ['customer-agent-write']
  },
  DisabledTooltipContent: () => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "assetActionsLibForms.tooltip.createCustomerAgentActionDisabled"
  }),
  ActionButton: CreateCustomerAgentActionButton
};
export const FORM_VIEW_ACTION = {
  id: FORM_ACTION_IDS.VIEW,
  name: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "assetActionsLibForms.actions.view"
  }),
  actionGroup: 1,
  orderInActionGroup: 0,
  displayPermissions: {
    custom: ({
      crmObject
    }) => {
      const formType = getPropertyValue(crmObject, 'hs_form_type');
      return formType !== FormTypes.FLOW && formType !== FormTypes.FACEBOOK_LEAD_AD && formType !== FormTypes.CAPTURED;
    }
  },
  ModalOrPanel: ViewFormModal
};
export const FORM_VIEW_DETAILS_ACTION = {
  id: FORM_ACTION_IDS.VIEW_DETAILS,
  name: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "assetActionsLibForms.actions.viewDetails"
  }),
  actionGroup: 1,
  orderInActionGroup: 1,
  displayPermissions: {
    custom: ({
      crmObject,
      nonCrmObject
    }) => {
      const formType = getPropertyValue(crmObject, 'hs_form_type');
      return Boolean(nonCrmObject && nonCrmObject.type !== 'SUBMISSIONS') && formType !== FormTypes.FACEBOOK_LEAD_AD && formType !== FormTypes.CAPTURED;
    }
  },
  ActionButton: props => {
    const {
      crmObject,
      onActionSuccess,
      nonCrmObject
    } = useAssetActions();
    const formGuid = getPropertyValue(crmObject, 'hs_form_id') || '';
    const handleClick = useCallback(() => {
      if (onActionSuccess) {
        onActionSuccess({
          actionId: FORM_ACTION_IDS.VIEW_DETAILS
        });
      }
    }, [onActionSuccess]);
    if (nonCrmObject && ['SUBMISSIONS', 'EDITOR'].includes(nonCrmObject.type)) {
      return /*#__PURE__*/_jsx(AppLink, {
        href: getPerformanceRoute(formGuid),
        onClick: handleClick,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "assetActionsLibForms.actions.viewDetails"
        })
      });
    }
    return /*#__PURE__*/_jsx(Link, Object.assign({}, props, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "assetActionsLibForms.actions.viewDetails"
      })
    }));
  }
};
export const FORM_COMMENT_ACTION = Object.assign({}, COMMENT_ACTION, {
  actionGroup: 1,
  orderInActionGroup: 2,
  displayPermissions: {
    scopes: ['commenting-access-marketing-hub'],
    custom: ({
      nonCrmObject
    }) => Boolean(nonCrmObject && ['EDITOR', 'SUBMISSIONS'].includes(nonCrmObject.type))
  }
});
export const FORM_VIEW_SUBMISSIONS_ACTION = {
  id: FORM_ACTION_IDS.VIEW_SUBMISSIONS,
  name: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "assetActionsLibForms.actions.viewSubmissions"
  }),
  actionGroup: ({
    crmObject,
    layoutUse
  }) => {
    if (!layoutUse || layoutUse === 'detail-actions') {
      return 1;
    }
    const formType = getPropertyValue(crmObject, 'hs_form_type');
    if (formType === FormTypes.CAPTURED) {
      return 2;
    }
    return 1;
  },
  orderInActionGroup: ({
    crmObject,
    layoutUse
  }) => {
    if (!layoutUse || layoutUse === 'detail-actions') {
      return 3;
    }
    const formType = getPropertyValue(crmObject, 'hs_form_type');
    if (formType === FormTypes.CAPTURED) {
      return 2;
    }
    return 3;
  },
  displayPermissions: {
    custom: ({
      crmObject,
      nonCrmObject
    }) => {
      const formType = getPropertyValue(crmObject, 'hs_form_type');
      return Boolean(nonCrmObject && nonCrmObject.type !== 'SUBMISSIONS') && formType !== FormTypes.FACEBOOK_LEAD_AD;
    }
  },
  ActionButton: () => {
    const {
      crmObject,
      onActionSuccess
    } = useAssetActions();
    const formGuid = getPropertyValue(crmObject, 'hs_form_id') || '';
    const handleClick = useCallback(() => {
      if (onActionSuccess) {
        onActionSuccess({
          actionId: FORM_ACTION_IDS.VIEW_SUBMISSIONS
        });
      }
    }, [onActionSuccess]);
    return /*#__PURE__*/_jsx(AppLink, {
      href: getSubmissionsRoute(formGuid),
      onClick: handleClick,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "assetActionsLibForms.actions.viewSubmissions"
      })
    });
  }
};
export const FORM_EXPORT_SUBMISSIONS_ACTION = {
  id: FORM_ACTION_IDS.EXPORT_SUBMISSIONS,
  name: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "assetActionsLibForms.actions.exportSubmissions"
  }),
  actionGroup: ({
    crmObject,
    layoutUse
  }) => {
    if (!layoutUse || layoutUse === 'detail-actions') {
      return 1;
    }
    const formType = getPropertyValue(crmObject, 'hs_form_type');
    if (formType === FormTypes.CAPTURED) {
      return 2;
    }
    return 1;
  },
  orderInActionGroup: ({
    crmObject,
    layoutUse
  }) => {
    if (!layoutUse || layoutUse === 'detail-actions') {
      return 4;
    }
    const formType = getPropertyValue(crmObject, 'hs_form_type');
    if (formType === FormTypes.CAPTURED) {
      return 2;
    }
    return 4;
  },
  displayPermissions: {
    custom: ({
      nonCrmObject
    }) => {
      return Boolean(nonCrmObject && nonCrmObject.type !== 'EDITOR');
    }
  },
  enablePermissions: {
    custom: ({
      scopes
    }) => !scopes.includes('crm-secondary-permissions') || scopes.includes('crm-export')
  },
  ModalOrPanel: SubmissionExportDialog,
  DisabledTooltipContent: () => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "assetActionsLibForms.tooltip.exportSubmissions"
  })
};
export const FORM_VIEW_FILE_UPLOADS_ACTION = {
  id: FORM_ACTION_IDS.VIEW_FILE_UPLOADS,
  name: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "assetActionsLibForms.actions.viewFileUploads"
  }),
  actionGroup: 1,
  orderInActionGroup: 5,
  displayPermissions: {
    custom: ({
      crmObject,
      nonCrmObject
    }) => {
      const isAlpha = getPropertyValue(crmObject, 'hs_embed_version') === EmbedVersions.V4;
      if (!nonCrmObject || isAlpha) {
        return false;
      }
      const formType = getPropertyValue(crmObject, 'hs_form_type');
      if (nonCrmObject.type === 'DASHBOARD') {
        return formType !== FormTypes.FLOW && formType !== FormTypes.CAPTURED;
      }
      return Boolean(nonCrmObject.hasFileManagerUrl);
    }
  },
  ActionButton: props => {
    const {
      crmObject
    } = useAssetActions();
    const formGuid = getPropertyValue(crmObject, 'hs_form_id') || '';
    return /*#__PURE__*/_jsx(Button, Object.assign({
      href: getFormFileUploadsRoute(formGuid),
      use: "link"
    }, props, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "assetActionsLibForms.actions.viewFileUploads"
      })
    }));
  }
};
export const FORM_CREATE_LIST_ACTION = {
  id: FORM_ACTION_IDS.CREATE_LIST,
  name: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "assetActionsLibForms.actions.createList"
  }),
  actionGroup: 1,
  orderInActionGroup: 6,
  displayPermissions: {
    custom: ({
      crmObject,
      nonCrmObject
    }) => {
      const formType = getPropertyValue(crmObject, 'hs_form_type');
      if (nonCrmObject && nonCrmObject.type === 'DASHBOARD') {
        return formType !== FormTypes.FLOW && formType !== FormTypes.CAPTURED;
      }
      return true;
    }
  },
  ActionButton: props => {
    return /*#__PURE__*/_jsx(Link, Object.assign({}, props, {
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "assetActionsLibForms.actions.createList"
      })
    }));
  },
  ModalOrPanel: ({
    onClose,
    gates,
    crmObject
  }) => {
    const guid = getPropertyValue(crmObject, 'hs_form_id');
    return /*#__PURE__*/_jsx(ApolloProvider, {
      client: apolloClient,
      children: /*#__PURE__*/_jsx(ListCreationModal, {
        processingType: "DYNAMIC",
        getIsUngated: gateArg => gates.includes(gateArg),
        onCancel: onClose,
        filterBranch: getContactListFilterBranch(guid)
      })
    });
  }
};
export const FORM_CREATE_TRANSLATION_ACTION = {
  id: FORM_ACTION_IDS.CREATE_TRANSLATION,
  name: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "assetActionsLibForms.actions.createTranslation"
  }),
  actionGroup: 1,
  orderInActionGroup: 8,
  displayPermissions: {
    custom: ({
      crmObject
    }) => getPropertyValue(crmObject, 'hs_cloneable') === 'true' && getPropertyValue(crmObject, 'hs_form_type') === FormTypes.HUBSPOT && getPropertyValue(crmObject, 'hs_embed_version') !== EmbedVersions.V4
  },
  enablePermissions: {
    custom: ({
      crmObject,
      scopes,
      nonCrmObject
    }) => {
      const formsWriteAccess = scopes.includes('forms-write');
      const formType = getPropertyValue(crmObject, 'hs_form_type');
      const isFormCloneable = getPropertyValue(crmObject, 'hs_cloneable') === 'true' && formType !== FormTypes.TICKET_FORM && formType !== FormTypes.BLOG_COMMENT;
      if (nonCrmObject && nonCrmObject.type === 'DASHBOARD') {
        return isFormCloneable && formsWriteAccess && formType !== FormTypes.FLOW && formType !== FormTypes.CAPTURED;
      }
      return isFormCloneable && formsWriteAccess;
    }
  },
  DisabledTooltipContent: ({
    crmObject
  }) => {
    const formType = getPropertyValue(crmObject, 'hs_form_type');
    const isFormCloneable = getPropertyValue(crmObject, 'hs_cloneable') === 'true' && formType !== FormTypes.TICKET_FORM && formType !== FormTypes.BLOG_COMMENT;
    return isFormCloneable ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: "assetActionsLibForms.tooltip.noPermission"
    }) : /*#__PURE__*/_jsx(FormattedMessage, {
      message: "assetActionsLibForms.tooltip.formNotTranslatable"
    });
  },
  ModalOrPanel: TranslateFormDialog
};
export const FORM_SELECT_CAMPAIGN_ACTION = {
  id: FORM_ACTION_IDS.SELECT_CAMPAIGN,
  displayPermissions: {
    custom: ({
      crmObject,
      nonCrmObject
    }) => {
      const formType = getPropertyValue(crmObject, 'hs_form_type');
      if (nonCrmObject && nonCrmObject.type === 'DASHBOARD') {
        return formType !== FormTypes.CAPTURED && formType !== FormTypes.FLOW;
      }
      return formType !== FormTypes.FACEBOOK_LEAD_AD;
    }
  },
  enablePermissions: {
    scopes: ['campaigns-access']
  },
  DisabledTooltipContent: () => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "assetActionsLibForms.tooltip.noPermission"
  }),
  name: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "assetActionsLibForms.actions.selectCampaign"
  }),
  ModalOrPanel: SelectCampaignModal,
  actionGroup: 1,
  orderInActionGroup: 9
};
export const FORM_EDIT_ACTION = {
  id: ACTION_IDS.EDIT,
  name: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "assetActionsLibForms.actions.edit"
  }),
  displayPermissions: {
    custom: ({
      crmObject,
      nonCrmObject
    }) => {
      if (!nonCrmObject || nonCrmObject.type === 'EDITOR') {
        return false;
      }
      const formType = getPropertyValue(crmObject, 'hs_form_type');
      if (formType === FormTypes.FACEBOOK_LEAD_AD) {
        return false;
      }
      if (nonCrmObject.type === 'DASHBOARD') {
        return formType !== FormTypes.CAPTURED;
      }
      return true;
    }
  },
  enablePermissions: {
    custom: ({
      crmObject
    }) => {
      const formEditable = getPropertyValue(crmObject, 'hs_editable') === 'true';
      return formEditable;
    }
  },
  actionGroup: 1,
  ActionButton: EditActionButton,
  DisabledTooltipContent: () => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "assetActionsLibForms.tooltip.formNotEditable"
  })
};
export const FORM_COMPARE_ACTION = {
  id: ACTION_IDS.COMPARE,
  name: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "assetActionsLibForms.actions.compare"
  }),
  actionGroup: 1,
  orderInActionGroup: 10,
  displayPermissions: {
    custom: ({
      crmObject,
      nonCrmObject
    }) => {
      if (!nonCrmObject) {
        return false;
      }
      const formType = getPropertyValue(crmObject, 'hs_form_type');
      if (nonCrmObject.type === 'DASHBOARD') {
        return formType !== FormTypes.CAPTURED;
      }
      return nonCrmObject.type !== 'EDITOR' && formType !== FormTypes.FACEBOOK_LEAD_AD;
    }
  },
  ActionButton: ({
    disabled
  }) => {
    const {
      crmObject,
      onActionSuccess
    } = useAssetActions();
    const {
      scopes
    } = useUserInfo();
    const {
      openUpgradeModal
    } = useUpgradeModal('forms-conversion-charts');
    const hasPql = !scopes.includes('comparison-tool-access');
    return /*#__PURE__*/_jsxs(Link, {
      disabled: disabled,
      href: hasPql ? undefined : getCompareRoute(crmObject),
      onClick: () => {
        if (onActionSuccess) {
          onActionSuccess({
            actionId: ACTION_IDS.COMPARE,
            data: crmObject
          });
        }
        if (hasPql) {
          openUpgradeModal();
        }
      },
      children: [/*#__PURE__*/_jsx(FormattedMessage, {
        message: "assetActionsLibForms.actions.compare"
      }), hasPql && /*#__PURE__*/_jsx(LockBadge, {})]
    });
  }
};
export const FORM_MANAGE_ACCESS_ACTION = Object.assign({}, MANAGE_ACCESS_ACTION, {
  displayPermissions: {
    scopes: ['content-team-assignment', 'teams-form-partitioning'],
    custom: ({
      crmObject
    }) => {
      const formType = getPropertyValue(crmObject, 'hs_form_type');
      return formType !== FormTypes.FACEBOOK_LEAD_AD;
    }
  },
  actionGroup: ({
    crmObject,
    layoutUse
  }) => {
    if (!layoutUse || layoutUse === 'detail-actions') {
      return 2;
    }
    const formType = getPropertyValue(crmObject, 'hs_form_type');
    if (formType === FormTypes.CAPTURED) {
      return 3;
    }
    return 2;
  },
  orderInActionGroup: 0,
  ModalOrPanel: ({
    onClose
  }) => {
    const {
      crmObject,
      onActionSuccess
    } = useAssetActions();
    const {
      scopes
    } = useUserInfo();
    const hasBusinessUnitAccess = useMemo(() => scopes.includes('business-units-access'), [scopes]);
    const formGuid = useMemo(() => getPropertyValue(crmObject, 'hs_form_id') || '', [crmObject]);
    const businessUnitId = useMemo(() => parseInt(getPropertyValue(crmObject, 'hs_all_assigned_business_unit_ids') || '0', 10), [crmObject]);
    const handleTrackInteraction = useCallback(trackingString => {
      if (onActionSuccess) {
        onActionSuccess({
          actionId: MANAGE_ACCESS_ACTION.id,
          data: {
            trackingString
          }
        });
      }
    }, [onActionSuccess]);
    return /*#__PURE__*/_jsx(FormPermissionsActionPanel, {
      formGuids: [formGuid],
      onClosePanel: onClose,
      BusinessUnitTagContainer: BusinessUnitTagContainer,
      track: handleTrackInteraction,
      formBusinessUnitsIds: [businessUnitId],
      hasBusinessUnitsAccess: hasBusinessUnitAccess
    });
  }
});
export const FORM_DELETE_ACTION = Object.assign({}, DELETE_ACTION, {
  displayPermissions: {
    custom: ({
      crmObject
    }) => {
      const formType = getPropertyValue(crmObject, 'hs_form_type');
      return formType !== FormTypes.FACEBOOK_LEAD_AD;
    }
  },
  enablePermissions: {
    scopes: ['forms-write'],
    custom: ({
      crmObject
    }) => {
      const formDeletable = getPropertyValue(crmObject, 'hs_deletable');
      return !!formDeletable && formDeletable !== 'false';
    }
  },
  DisabledTooltipContent: ({
    crmObject
  }) => {
    const formDeletable = getPropertyValue(crmObject, 'hs_deletable');
    const formType = getPropertyValue(crmObject, 'hs_form_type');
    if (formDeletable && formDeletable === 'false') {
      return formType === FormTypes.BLOG_COMMENT ? /*#__PURE__*/_jsx(FormattedMessage, {
        message: "assetActionsLibForms.tooltip.blogCommentNotDeletable"
      }) : /*#__PURE__*/_jsx(FormattedMessage, {
        message: "assetActionsLibForms.tooltip.formNotDeletable"
      });
    }
    return /*#__PURE__*/_jsx(FormattedMessage, {
      message: "assetActionsLibForms.tooltip.noPermission"
    });
  },
  ModalOrPanel: FormDeleteModal,
  actionGroup: ({
    crmObject,
    layoutUse
  }) => {
    if (!layoutUse || layoutUse === 'detail-actions') {
      return 3;
    }
    const formType = getPropertyValue(crmObject, 'hs_form_type');
    if (formType === FormTypes.CAPTURED) {
      return 4;
    }
    return 3;
  }
});
export const FORM_CLONE_ACTION = {
  id: FORM_ACTION_IDS.CLONE,
  name: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "assetActionsLibForms.actions.clone"
  }),
  actionGroup: 1,
  orderInActionGroup: 7,
  displayPermissions: {
    custom: ({
      crmObject,
      nonCrmObject,
      gates
    }) => {
      if (!nonCrmObject) {
        return false;
      }
      const formType = getPropertyValue(crmObject, 'hs_form_type');
      if (formType === FormTypes.FACEBOOK_LEAD_AD) {
        return false;
      }
      if (nonCrmObject.type === 'DASHBOARD') {
        return formType !== FormTypes.CAPTURED;
      }
      if (nonCrmObject.type === 'NEW_EDITOR') {
        return gates.includes(gate('VisExpEditor:Clone'));
      }
      return Boolean(['EDITOR', 'SUBMISSIONS', 'NEW_EDITOR'].includes(nonCrmObject.type));
    }
  },
  enablePermissions: {
    custom: ({
      crmObject,
      gates,
      scopes
    }) => {
      const formsWriteAccess = scopes.includes('forms-write');
      const hasNewEditorAccess = gates.includes('Forms:NewEditorAccess');
      const formType = crmObject ? getPropertyValue(crmObject, 'hs_form_type') : null;
      const isFormCloneable = crmObject && getPropertyValue(crmObject, 'hs_cloneable') === 'true' && ![FormTypes.TICKET_FORM, FormTypes.BLOG_COMMENT].includes(
      // @ts-expect-error weird type condition
      formType);
      const isSunsettingPopupForms = scopes.includes('calls-to-action-read');
      const isPreviewForm = getPropertyValue(crmObject, 'hs_embed_version') === 'v4';
      return Boolean(formsWriteAccess && isFormCloneable && (!isPreviewForm || hasNewEditorAccess) && (formType !== FormTypes.FLOW || !isSunsettingPopupForms));
    }
  },
  ActionButton: props => {
    const {
      layoutUse
    } = useAssetActions();
    return /*#__PURE__*/_jsx(Button, Object.assign({}, props, {
      "data-action": "clone"
    }, layoutUse !== 'hover-actions' && {
      use: 'link'
    }, {
      "data-test-id": "clone",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: layoutUse === 'hover-actions' || layoutUse === 'editor-actions' ? 'assetActionsLibForms.actions.clone' : 'assetActionsLibForms.actions.cloneForm'
      })
    }));
  },
  DisabledTooltipContent: ({
    scopes,
    gates,
    crmObject
  }) => {
    // Compute permissions
    const isPreviewForm = getPropertyValue(crmObject, 'hs_embed_version') === 'v4';
    const hasNewEditorAccess = gates.includes('Forms:NewEditorAccess');
    const isSunsettingPopupForms = scopes.includes('calls-to-action-read');
    const formType = getPropertyValue(crmObject, 'hs_form_type');
    const formsWriteAccess = scopes.includes('forms-write');
    const isFormCloneable = getPropertyValue(crmObject, 'hs_cloneable') === 'true' && ![FormTypes.TICKET_FORM, FormTypes.BLOG_COMMENT].includes(
    // @ts-expect-error weird type condition
    formType);
    if (isPreviewForm && !hasNewEditorAccess) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "assetActionsLibForms.modals.cloneDialog.formNotCloneableAlphaEditor"
      });
    } else if (isSunsettingPopupForms && formType === FormTypes.FLOW) {
      return /*#__PURE__*/_jsxs(_Fragment, {
        children: [/*#__PURE__*/_jsx(FormattedMessage, {
          message: "assetActionsLibForms.modals.cloneDialog.formNotCloneableSunsettingPopups.title"
        }), /*#__PURE__*/_jsx(DocLink, {
          path: CREATE_POP_UP_CALLS_TO_ACTION,
          children: /*#__PURE__*/_jsx(FormattedMessage, {
            message: "assetActionsLibForms.modals.cloneDialog.formNotCloneableSunsettingPopups.learnMore"
          })
        })]
      });
    } else if (!isFormCloneable) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "assetActionsLibForms.modals.cloneDialog.formNotCloneable"
      });
    } else if (!formsWriteAccess) {
      return /*#__PURE__*/_jsx(FormattedMessage, {
        message: "assetActionsLibForms.tooltip.noPermission"
      });
    }
    return null;
  },
  ModalOrPanel: CloneFormDialog
};
export const FORM_SHARE_ACTION = {
  id: FORM_ACTION_IDS.SHARE,
  name: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "assetActionsLibForms.actions.share"
  }),
  actionGroup: 1,
  orderInActionGroup: 2,
  displayPermissions: {
    custom: ({
      crmObject,
      nonCrmObject
    }) => {
      if (!nonCrmObject || !['DASHBOARD', 'EDITOR'].includes(nonCrmObject.type)) {
        return false;
      }
      const formType = getPropertyValue(crmObject, 'hs_form_type');
      return formType !== FormTypes.FLOW && formType !== FormTypes.CAPTURED;
    }
  },
  ModalOrPanel: ShareFormDialog
};
export const FORM_RENAME_ACTION = {
  id: FORM_ACTION_IDS.RENAME,
  name: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "assetActionsLibForms.actions.rename"
  }),
  actionGroup: 1,
  orderInActionGroup: 0,
  displayPermissions: {
    custom: ({
      crmObject
    }) => {
      const formType = getPropertyValue(crmObject, 'hs_form_type');
      return formType === FormTypes.CAPTURED;
    }
  },
  enablePermissions: {
    scopes: ['forms-write']
  },
  DisabledTooltipContent: () => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "assetActionsLibForms.tooltip.noPermission"
  }),
  ModalOrPanel: CollectedFormRenameDialog
};
export const FORM_EDIT_NON_HS_MAILCHIMP_LIST = {
  id: FORM_ACTION_IDS.EDIT_NON_HS_MAILCHIMP_LIST,
  name: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "assetActionsLibForms.actions.editMailchimpLists",
    options: {
      count: 0
    }
  }),
  actionGroup: 2,
  orderInActionGroup: 3,
  displayPermissions: {
    custom: ({
      crmObject,
      nonCrmObject
    }) => {
      const formType = getPropertyValue(crmObject, 'hs_form_type');
      return Boolean(nonCrmObject && nonCrmObject.type === 'DASHBOARD' && formType === FormTypes.CAPTURED);
    }
  },
  enablePermissions: {
    scopes: ['forms-write']
  },
  DisabledTooltipContent: () => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "assetActionsLibForms.tooltip.noPermission"
  }),
  ActionButton: EditNonHSMailchimpListActionButton,
  ModalOrPanel: ESPSettingsDialog
};
export const FORM_EDIT_NON_HS_EMAIL_NOTIFICATIONS_ACTION = {
  id: FORM_ACTION_IDS.EDIT_NON_HS_EMAIL_NOTIFICATIONS,
  name: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "assetActionsLibForms.actions.editEmail"
  }),
  actionGroup: 2,
  orderInActionGroup: 4,
  displayPermissions: {
    custom: ({
      crmObject,
      nonCrmObject
    }) => {
      const formType = getPropertyValue(crmObject, 'hs_form_type');
      return Boolean(nonCrmObject && nonCrmObject.type === 'DASHBOARD' && formType === FormTypes.CAPTURED);
    }
  },
  ActionButton: props => /*#__PURE__*/_jsx(Link, Object.assign({}, props, {
    href: getCollectedFormsSettingsPage(),
    children: /*#__PURE__*/_jsx(FormattedMessage, {
      message: "assetActionsLibForms.actions.editEmail"
    })
  }))
};
export const FORM_UNPUBLISH_ACTION = {
  id: FORM_ACTION_IDS.UNPUBLISH,
  name: /*#__PURE__*/_jsx(FormattedMessage, {
    message: "assetActionsLibForms.actions.unpublishForm"
  }),
  actionGroup: 2,
  orderInActionGroup: 0,
  displayPermissions: {
    custom: ({
      crmObject,
      nonCrmObject
    }) => {
      const formType = getPropertyValue(crmObject, 'hs_form_type');
      const formStatus = getPropertyValue(crmObject, 'hs_status');
      return Boolean(nonCrmObject && nonCrmObject.type === 'DASHBOARD' && formType === FormTypes.FLOW) && formStatus === 'PUBLISHED';
    }
  },
  enablePermissions: {
    scopes: ['forms-write']
  },
  DisabledTooltipContent: () => /*#__PURE__*/_jsx(FormattedMessage, {
    message: "assetActionsLibForms.tooltip.noPermission"
  }),
  ActionButton: UnpublishActionButton
};
export const formActions = [FORM_SUMMARIZE_ACTION, FORM_CREATE_CUSTOMER_AGENT_ACTION, FORM_VIEW_ACTION, FORM_VIEW_DETAILS_ACTION, FORM_COMMENT_ACTION, FORM_VIEW_SUBMISSIONS_ACTION, FORM_EXPORT_SUBMISSIONS_ACTION, FORM_VIEW_FILE_UPLOADS_ACTION, FORM_CREATE_LIST_ACTION, FORM_CLONE_ACTION, FORM_CREATE_TRANSLATION_ACTION, FORM_SELECT_CAMPAIGN_ACTION, FORM_EDIT_ACTION, FORM_COMPARE_ACTION, FORM_MANAGE_ACCESS_ACTION, FORM_DELETE_ACTION, FORM_SHARE_ACTION, FORM_RENAME_ACTION, FORM_EDIT_NON_HS_MAILCHIMP_LIST, FORM_EDIT_NON_HS_EMAIL_NOTIFICATIONS_ACTION, FORM_UNPUBLISH_ACTION];